import { useTenantQuery } from "generated/graphql";
import type { GraphQLErrorExtensions } from "graphql/error/GraphQLError";
import type { ChildrenProps } from "helpers";
import SessionStorage from "helpers/SessionStorage";
import type React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "src/state/appStore";

const ReactTenantProvider: React.FC<ChildrenProps> = ({ children }) => {
	const navigate = useNavigate();

	const tenantId = useAppStore(s => s.tenantId);
	const setTenantId = useAppStore(s => s.setTenantId);
	const tenantName = useAppStore(s => s.tenantName);
	const setTenantName = useAppStore(s => s.setTenantName);

	const isAuthenticated = useAppStore(s => s.authIsAuthenticated);
	const isLoading = useAppStore(s => s.authIsLoading);

	useEffect(() => {
		const urlTenant = window.location.pathname.split("/")[1];
		if (!urlTenant) return;

		if (urlTenant === "logout") {
			const pathName = SessionStorage.getLocation();
			setTenantName(pathName.split("/")[1]);
			navigate(SessionStorage.getLocation());
		} else if (urlTenant !== tenantName) {
			setTenantName(urlTenant);
		}
	}, [tenantName, navigate, setTenantName]);

	const vars = useMemo(() => ({ name: tenantName ?? "" }), [tenantName]);

	const skip = !isAuthenticated || isLoading || !tenantName || !!tenantId;

	const { data, error, loading, networkStatus } = useTenantQuery({
		skip,
		variables: vars,
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true
	});

	useEffect(() => {
		if (skip || !error) return;

		let errorCode = 0;
		let errorText = "";
		let errorMessage = "";

		const gqlErrors: any[] = (error as any)?.graphQLErrors ?? [];
		if (gqlErrors.length > 0) {
			const { message, extensions } = gqlErrors[0];
			const errorDetail: GraphQLErrorExtensions | undefined = extensions as any;
			const nex = (errorDetail as any)?.exception;

			if (message?.includes("503")) errorCode = 503;
			else if (message?.includes("404")) errorCode = 404;

			if (errorDetail) {
				errorCode = errorCode === 0 ? 500 : errorCode;
				try {
					errorText = (nex as any)?.errno ?? "";
					errorMessage = (nex as any)?.message ?? "";
				} catch {
					/* ignore */
				}
			}
		}
	}, [skip, error]);

	// completion
	const didSet = useRef(false);
	useEffect(() => {
		if (skip) return;

		const id = data?.tenant?.id;
		if (!id) return;

		if (didSet.current) return;
		didSet.current = true;

		setTenantId(id);
	}, [skip, data?.tenant?.id, setTenantId]);

	return <>{children}</>;
};

export default ReactTenantProvider;